import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMeeting, API } from "../App";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  FileText,
  Download,
  ArrowLeft,
  User,
  Building2,
  Calendar,
  StickyNote,
  Zap,
  Settings2,
  Cpu,
  AlertTriangle,
  TestTube,
  Radio,
  Loader2,
  HelpCircle,
  CheckSquare,
  Clock,
  MessageSquare,
  ArrowRight,
  Check
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { TRANSFORMER_DATA } from "../data/transformerData";

const SummaryPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { currentMeeting, selectedAssets, discussionPoints, questions, actionItems } = useMeeting();
  const [assetDetails, setAssetDetails] = useState({
    transformers: null,
    switchgear: null,
    motors: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const summaryRef = useRef(null);

  useEffect(() => {
    fetchAllAssetData();
  }, []);

  const fetchAllAssetData = async () => {
    try {
      const [transformers, switchgear, motors] = await Promise.all([
        axios.get(`${API}/assets/transformers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/assets/switchgear`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/assets/motors`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setAssetDetails({
        transformers: transformers.data,
        switchgear: switchgear.data,
        motors: motors.data
      });
    } catch (error) {
      console.error("Failed to load asset data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getItemDetails = (type, id, assetCategory) => {
    // The ID format is "componentId-itemName" (e.g., "1-Overheating")
    const parts = id.split('-');
    if (parts.length < 2) return null;
    
    const componentId = parts[0];
    const itemName = parts.slice(1).join('-'); // Rejoin in case item name has hyphens
    
    // Look up from component data (TRANSFORMER_DATA, etc.)
    if (assetCategory === 'transformers') {
      const component = TRANSFORMER_DATA.find(c => c.id === componentId);
      if (!component) {
        // Return basic info even if component not found
        return { id, name: itemName, description: '', component_name: `Component ${componentId}` };
      }
      
      let item = null;
      switch (type) {
        case "failure_mode":
          item = component.failureModes?.find(f => f.name === itemName);
          break;
        case "test":
          item = component.tests?.find(t => t.name === itemName);
          break;
        case "monitoring":
          item = component.monitoring?.find(m => m.name === itemName);
          break;
      }
      
      if (item) {
        return {
          id,
          name: item.name,
          description: item.description || '',
          importance: item.importance || '',
          approach: item.approach || '',
          severity: item.severity || item.risk_level || '',
          risk_level: item.severity || item.risk_level || '',
          full_name: item.fullName || item.name,
          data_points: item.dataPoints || '',
          benefits: item.benefits || '',
          component_name: component.name,
          images: item.images || []
        };
      }
      
      // Fallback if item not found in component data
      return { id, name: itemName, description: '', component_name: component.name };
    }
    
    // For other asset types, try the API data
    const data = assetDetails[assetCategory];
    if (!data) return { id, name: itemName, description: '' };

    let foundItem = null;
    switch (type) {
      case "failure_mode":
        foundItem = data.failure_modes?.find((f) => f.name === itemName);
        break;
      case "test":
        foundItem = data.diagnostic_tests?.find((t) => t.name === itemName);
        break;
      case "monitoring":
        foundItem = data.monitoring_solutions?.find((m) => m.name === itemName);
        break;
    }
    
    return foundItem || { id, name: itemName, description: '' };
  };

  const groupedAssets = selectedAssets.reduce((acc, asset) => {
    if (!acc[asset.asset]) {
      acc[asset.asset] = { failure_modes: [], tests: [], monitoring: [] };
    }
    if (asset.type === "failure_mode") {
      acc[asset.asset].failure_modes.push(asset);
    } else if (asset.type === "test") {
      acc[asset.asset].tests.push(asset);
    } else if (asset.type === "monitoring") {
      acc[asset.asset].monitoring.push(asset);
    }
    return acc;
  }, {});

  const assetConfig = {
    transformers: { icon: Zap, color: "text-amber-600", bgColor: "bg-amber-50", label: "Transformers" },
    switchgear: { icon: Settings2, color: "text-blue-600", bgColor: "bg-blue-50", label: "Switchgear" },
    motors: { icon: Cpu, color: "text-green-600", bgColor: "bg-green-50", label: "Motors" }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleExportPdf = async () => {
    setIsPdfGenerating(true);
    try {
      const printWindow = window.open('', '_blank');
      
      // Generate Questions HTML
      const questionsHtml = questions?.length > 0 ? `
        <h2>Customer Questions & Answers</h2>
        <div class="section">
          ${questions.map(q => `
            <div class="item">
              <div class="question-header">
                <span class="item-name">Q: ${q.question}</span>
                <span class="priority-badge priority-${q.priority}">${q.priority}</span>
              </div>
              ${q.answer ? `
                <div class="answer">
                  <strong>A:</strong> ${q.answer}
                </div>
              ` : '<div class="answer pending">Pending response</div>'}
            </div>
          `).join('')}
        </div>
      ` : '';

      // Generate Action Items HTML
      const actionsHtml = actionItems?.length > 0 ? `
        <h2>Action Items & Next Steps</h2>
        <div class="section">
          ${actionItems.map(a => `
            <div class="item ${a.status === 'completed' ? 'completed' : ''}">
              <div class="action-header">
                <span class="item-name">${a.description}</span>
                <span class="status-badge status-${a.status}">${a.status.replace('_', ' ')}</span>
              </div>
              ${a.assigned_to ? `<div class="assignee">Assigned to: ${a.assigned_to}</div>` : ''}
            </div>
          `).join('')}
        </div>
      ` : '';

      // Generate Discussion Timeline HTML
      const timelineHtml = discussionPoints?.length > 0 ? `
        <h2>Discussion Timeline</h2>
        <div class="timeline">
          ${discussionPoints.map(dp => `
            <div class="timeline-item">
              <span class="time">${formatTime(dp.timestamp)}</span>
              <span class="text">${dp.text}</span>
            </div>
          `).join('')}
        </div>
      ` : '';
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Asset Diagnostic Summary - ${currentMeeting?.customer_name}</title>
          <style>
            body { 
              font-family: 'Inter', -apple-system, sans-serif; 
              padding: 40px; 
              color: #0f172a;
              line-height: 1.6;
            }
            h1 { font-size: 28px; color: #1e3a8a; margin-bottom: 8px; }
            h2 { font-size: 20px; color: #1e3a8a; margin-top: 24px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; }
            h3 { font-size: 16px; color: #334155; margin-top: 16px; }
            .header { border-bottom: 2px solid #1e3a8a; padding-bottom: 20px; margin-bottom: 30px; }
            .customer-info { display: flex; gap: 40px; margin-bottom: 20px; flex-wrap: wrap; }
            .info-item { min-width: 150px; }
            .info-label { font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
            .info-value { font-size: 16px; font-weight: 500; }
            .section { margin-bottom: 24px; padding: 16px; background: #f8fafc; border-radius: 4px; }
            .item { margin-bottom: 12px; padding: 12px; background: white; border: 1px solid #e2e8f0; border-radius: 4px; }
            .item.completed { background: #f0fdf4; border-color: #86efac; }
            .item-name { font-weight: 600; margin-bottom: 4px; }
            .item-desc { font-size: 14px; color: #64748b; }
            .notes-section { background: #fefce8; border: 1px solid #fef08a; padding: 16px; border-radius: 4px; margin-top: 24px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #94a3b8; text-align: center; }
            
            /* Questions & Actions styles */
            .question-header, .action-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
            .answer { font-size: 14px; color: #374151; padding-left: 16px; border-left: 3px solid #3b82f6; margin-top: 8px; }
            .answer.pending { color: #9ca3af; font-style: italic; }
            .priority-badge, .status-badge { font-size: 11px; padding: 2px 8px; border-radius: 4px; text-transform: uppercase; }
            .priority-high { background: #fee2e2; color: #b91c1c; }
            .priority-medium { background: #fef3c7; color: #b45309; }
            .priority-low { background: #f3f4f6; color: #4b5563; }
            .status-pending { background: #f3f4f6; color: #4b5563; }
            .status-in_progress { background: #dbeafe; color: #1d4ed8; }
            .status-completed { background: #d1fae5; color: #047857; }
            .status-open { background: #dbeafe; color: #1d4ed8; }
            .status-answered { background: #d1fae5; color: #047857; }
            .status-follow_up { background: #fef3c7; color: #b45309; }
            .assignee { font-size: 13px; color: #6b7280; margin-top: 4px; }
            
            /* Timeline styles */
            .timeline { padding: 16px; background: #f8fafc; border-radius: 4px; }
            .timeline-item { display: flex; gap: 12px; padding: 8px 0; border-bottom: 1px solid #e2e8f0; }
            .timeline-item:last-child { border-bottom: none; }
            .timeline-item .time { font-size: 12px; color: #64748b; min-width: 60px; }
            .timeline-item .text { font-size: 14px; color: #374151; }
            
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Asset Diagnostic Summary</h1>
            <p style="color: #64748b;">Asset Atlas - CBM Diagnostic Engine</p>
          </div>
          
          <div class="customer-info">
            <div class="info-item">
              <div class="info-label">Customer</div>
              <div class="info-value">${currentMeeting?.customer_name}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Company</div>
              <div class="info-value">${currentMeeting?.company}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Date</div>
              <div class="info-value">${new Date().toLocaleDateString()}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Primary Focus</div>
              <div class="info-value">${currentMeeting?.primary_asset_focus}</div>
            </div>
          </div>
          
          ${Object.entries(groupedAssets).map(([assetType, items]) => `
            <h2>${assetConfig[assetType]?.label || assetType}</h2>
            
            ${items.failure_modes.length > 0 ? `
              <h3>Identified Failure Modes</h3>
              <div class="section">
                ${items.failure_modes.map((item) => {
                  const details = getItemDetails(item.type, item.id, item.asset);
                  return details ? `
                    <div class="item">
                      <div class="item-name">${details.name}</div>
                      <div class="item-desc">${details.description}</div>
                    </div>
                  ` : '';
                }).join('')}
              </div>
            ` : ''}
            
            ${items.tests.length > 0 ? `
              <h3>Recommended Diagnostic Tests</h3>
              <div class="section">
                ${items.tests.map((item) => {
                  const details = getItemDetails(item.type, item.id, item.asset);
                  return details ? `
                    <div class="item">
                      <div class="item-name">${details.name} (${details.full_name})</div>
                      <div class="item-desc">${details.description}</div>
                    </div>
                  ` : '';
                }).join('')}
              </div>
            ` : ''}
            
            ${items.monitoring.length > 0 ? `
              <h3>Proposed Monitoring Solutions</h3>
              <div class="section">
                ${items.monitoring.map((item) => {
                  const details = getItemDetails(item.type, item.id, item.asset);
                  return details ? `
                    <div class="item">
                      <div class="item-name">${details.name}</div>
                      <div class="item-desc">${details.description}</div>
                      <div style="margin-top: 8px; font-size: 13px; color: #059669;"><strong>Benefits:</strong> ${details.benefits}</div>
                    </div>
                  ` : '';
                }).join('')}
              </div>
            ` : ''}
          `).join('')}
          
          ${questionsHtml}
          
          ${actionsHtml}
          
          ${currentMeeting?.notes ? `
            <div class="notes-section">
              <h3 style="margin-top: 0;">Meeting Notes</h3>
              <p style="white-space: pre-wrap;">${currentMeeting.notes}</p>
            </div>
          ` : ''}
          
          ${timelineHtml}
          
          <div class="footer">
            Generated by Asset Atlas • ${new Date().toLocaleString()}
          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
        toast.success("PDF export ready");
      }, 250);
    } catch (error) {
      toast.error("Failed to generate PDF");
      console.error(error);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  if (!currentMeeting) {
    navigate("/landing");
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="summary-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-900 rounded-md flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-3xl font-semibold text-heading text-slate-900">Session Summary</h1>
            <p className="text-slate-500">Review and export your presentation summary</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Dashboard
          </Button>
          <Button
            onClick={handleExportPdf}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={isPdfGenerating}
            data-testid="export-pdf-btn"
          >
            {isPdfGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Export PDF
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Summary Content */}
      <div id="summary-content" ref={summaryRef}>
        {/* Customer Info Card */}
        <Card className="mb-6 border-slate-200" data-testid="customer-info-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-heading">Session Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-slate-400 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Customer</p>
                  <p className="font-medium text-slate-900">{currentMeeting.customer_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-slate-400 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Company</p>
                  <p className="font-medium text-slate-900">{currentMeeting.company}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-slate-400 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Date</p>
                  <p className="font-medium text-slate-900">
                    {new Date(currentMeeting.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-slate-400 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Primary Focus</p>
                  <p className="font-medium text-slate-900">{currentMeeting.primary_asset_focus}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Discussion Timeline */}
        {discussionPoints?.length > 0 && (
          <Card className="mb-6 border-slate-200" data-testid="discussion-timeline-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg text-heading">
                <Clock className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                Discussion Timeline
                <Badge className="ml-2 bg-slate-100 text-slate-600">{discussionPoints.length} items</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {discussionPoints.map((dp, index) => (
                  <div key={dp.id} className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded">
                    <span className="text-xs text-slate-400 mt-0.5 min-w-[50px]">
                      {formatTime(dp.timestamp)}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm text-slate-700">{dp.text}</p>
                      {dp.asset_type && (
                        <span className="text-xs text-slate-400">
                          {assetConfig[dp.asset_type]?.label}
                        </span>
                      )}
                    </div>
                    <Badge className={`text-xs ${
                      dp.status === "discussed" ? "bg-green-100 text-green-700" :
                      dp.status === "follow_up" ? "bg-amber-100 text-amber-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {dp.status.replace("_", " ")}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Selected Assets Summary */}
        {selectedAssets.length > 0 ? (
          Object.entries(groupedAssets).map(([assetType, items]) => {
            const config = assetConfig[assetType];
            if (!config) return null;
            const Icon = config.icon;

            return (
              <Card key={assetType} className="mb-6 border-slate-200" data-testid={`summary-${assetType}`}>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 text-lg text-heading">
                    <div className={`w-8 h-8 ${config.bgColor} rounded-md flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${config.color}`} strokeWidth={1.5} />
                    </div>
                    {config.label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Failure Modes */}
                  {items.failure_modes.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-red-500" strokeWidth={1.5} />
                        <h4 className="font-medium text-slate-700">Identified Failure Modes</h4>
                        <Badge className="bg-red-100 text-red-700 text-xs">{items.failure_modes.length}</Badge>
                      </div>
                      <div className="space-y-2">
                        {items.failure_modes.map((item) => {
                          const details = getItemDetails(item.type, item.id, item.asset);
                          return details ? (
                            <div key={item.id} className="p-3 bg-red-50 border border-red-100 rounded-md">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-slate-900">{details.name}</span>
                                <div className="flex items-center gap-2">
                                  {details.component_name && (
                                    <Badge className="text-xs bg-slate-100 text-slate-600 border-slate-200">
                                      {details.component_name}
                                    </Badge>
                                  )}
                                  {(details.severity || details.risk_level) && (
                                    <Badge className={`text-xs ${
                                      (details.severity || details.risk_level) === 'Critical' 
                                        ? 'bg-red-100 text-red-700 border-red-200'
                                        : 'bg-amber-100 text-amber-700 border-amber-200'
                                    }`}>
                                      {details.severity || details.risk_level}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {details.description && (
                                <p className="text-sm text-slate-600">{details.description}</p>
                              )}
                              {details.importance && (
                                <p className="text-xs text-red-600 mt-2">
                                  <span className="font-medium">Importance:</span> {details.importance}
                                </p>
                              )}
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Diagnostic Tests */}
                  {items.tests.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <TestTube className="w-4 h-4 text-blue-500" strokeWidth={1.5} />
                        <h4 className="font-medium text-slate-700">Recommended Diagnostic Tests</h4>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">{items.tests.length}</Badge>
                      </div>
                      <div className="space-y-2">
                        {items.tests.map((item) => {
                          const details = getItemDetails(item.type, item.id, item.asset);
                          return details ? (
                            <div key={item.id} className="p-3 bg-blue-50 border border-blue-100 rounded-md">
                              <div className="flex items-center justify-between mb-1">
                                <div>
                                  <span className="font-medium text-slate-900">{details.name}</span>
                                  {details.full_name && details.full_name !== details.name && (
                                    <span className="text-sm text-slate-500 ml-2">({details.full_name})</span>
                                  )}
                                </div>
                                {details.component_name && (
                                  <Badge className="text-xs bg-slate-100 text-slate-600 border-slate-200">
                                    {details.component_name}
                                  </Badge>
                                )}
                              </div>
                              {details.description && (
                                <p className="text-sm text-slate-600 mt-1">{details.description}</p>
                              )}
                              {details.data_points && (
                                <p className="text-xs text-blue-600 mt-2">
                                  <span className="font-medium">Data Points:</span> {details.data_points}
                                </p>
                              )}
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Monitoring Solutions */}
                  {items.monitoring.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Radio className="w-4 h-4 text-green-500" strokeWidth={1.5} />
                        <h4 className="font-medium text-slate-700">Proposed Monitoring Solutions</h4>
                        <Badge className="bg-green-100 text-green-700 text-xs">{items.monitoring.length}</Badge>
                      </div>
                      <div className="space-y-2">
                        {items.monitoring.map((item) => {
                          const details = getItemDetails(item.type, item.id, item.asset);
                          return details ? (
                            <div key={item.id} className="p-3 bg-green-50 border border-green-100 rounded-md">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-slate-900">{details.name}</span>
                                {details.component_name && (
                                  <Badge className="text-xs bg-slate-100 text-slate-600 border-slate-200">
                                    {details.component_name}
                                  </Badge>
                                )}
                              </div>
                              {details.description && (
                                <p className="text-sm text-slate-600 mt-1">{details.description}</p>
                              )}
                              {details.benefits && (
                                <p className="text-xs text-green-600 mt-2">
                                  <span className="font-medium">Benefits:</span> {details.benefits}
                                </p>
                              )}
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="mb-6 border-slate-200">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Items Selected</h3>
              <p className="text-slate-500 mb-6">
                Visit the asset pages to select failure modes, tests, and monitoring solutions for your summary.
              </p>
              <Button onClick={() => navigate("/dashboard")} className="bg-blue-600 hover:bg-blue-700">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Questions & Answers */}
        {questions?.length > 0 && (
          <Card className="mb-6 border-slate-200 border-l-4 border-l-purple-500" data-testid="questions-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg text-heading">
                <HelpCircle className="w-5 h-5 text-purple-600" strokeWidth={1.5} />
                Customer Questions & Answers
                <Badge className="ml-2 bg-purple-100 text-purple-700">{questions.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {questions.map((q) => (
                <div key={q.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="font-medium text-slate-900">Q: {q.question}</p>
                    <div className="flex gap-2">
                      <Badge className={`text-xs ${
                        q.priority === "high" ? "bg-red-100 text-red-700" :
                        q.priority === "medium" ? "bg-amber-100 text-amber-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        {q.priority}
                      </Badge>
                      <Badge className={`text-xs ${
                        q.status === "answered" ? "bg-green-100 text-green-700" :
                        q.status === "follow_up" ? "bg-amber-100 text-amber-700" :
                        "bg-blue-100 text-blue-700"
                      }`}>
                        {q.status.replace("_", " ")}
                      </Badge>
                    </div>
                  </div>
                  {q.answer ? (
                    <div className="pl-4 border-l-2 border-purple-300 mt-2">
                      <p className="text-slate-700"><strong>A:</strong> {q.answer}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic mt-2">Pending response</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Action Items */}
        {actionItems?.length > 0 && (
          <Card className="mb-6 border-slate-200 border-l-4 border-l-orange-500" data-testid="actions-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg text-heading">
                <CheckSquare className="w-5 h-5 text-orange-600" strokeWidth={1.5} />
                Action Items & Next Steps
                <Badge className="ml-2 bg-orange-100 text-orange-700">
                  {actionItems.filter(a => a.status !== "completed").length} pending
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {actionItems.map((a) => (
                <div 
                  key={a.id} 
                  className={`p-3 rounded-lg border flex items-start gap-3 ${
                    a.status === "completed" 
                      ? "bg-green-50 border-green-200" 
                      : "bg-white border-slate-200"
                  }`}
                >
                  <div className={`mt-0.5 ${a.status === "completed" ? "text-green-600" : "text-slate-400"}`}>
                    {a.status === "completed" ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <CheckSquare className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${a.status === "completed" ? "text-slate-500 line-through" : "text-slate-900"}`}>
                      {a.description}
                    </p>
                    {a.assigned_to && (
                      <p className="text-sm text-slate-500 mt-1">
                        <ArrowRight className="w-3 h-3 inline mr-1" />
                        {a.assigned_to}
                      </p>
                    )}
                  </div>
                  <Badge className={`text-xs ${
                    a.status === "completed" ? "bg-green-100 text-green-700" :
                    a.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>
                    {a.status.replace("_", " ")}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Meeting Notes */}
        {currentMeeting.notes && (
          <Card className="mb-6 border-slate-200 bg-amber-50" data-testid="meeting-notes-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg text-heading">
                <StickyNote className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                Meeting Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap">{currentMeeting.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SummaryPage;
